import { useLocalStorage } from "./useLocalStorage"
import { useEffect } from "react"

export function useIndexTableState<T>(
  key: string,
  defaultValue: T,
  permanentValues?: Partial<T>,
): [T, (partial: Partial<T>) => void, (updater: (prevState: T) => T) => void] {
  const [queryState, setQueryState] = useLocalStorage<{ key: string; value: T }>(
    "indexQueryState",
    { key, value: { ...defaultValue, ...permanentValues } },
  )

  useEffect(() => {
    if (key !== queryState.key) {
      setQueryState({ key, value: { ...defaultValue, ...permanentValues } })
    } else if (permanentValues) {
      setQueryState({ key, value: { ...queryState.value, ...permanentValues } })
    }
  }, [])

  function set(partial: Partial<typeof defaultValue>) {
    setQueryState(prevState => {
      const currentValue =
        prevState.key === key ? prevState.value : { ...defaultValue, ...permanentValues }
      let resetPagination = {}
      if ("query" in partial && "pagination" in (currentValue as any)) {
        resetPagination = {
          pagination: { page: 1, perPage: (currentValue as any).pagination.perPage },
        }
      }
      return {
        key,
        value: {
          ...currentValue,
          ...partial,
          ...resetPagination,
        },
      }
    })
  }

  function setState(updater: (prevState: typeof defaultValue) => typeof defaultValue) {
    if (typeof updater === "function") {
      setQueryState(prev => {
        const currentValue = prev.key === key ? prev.value : { ...defaultValue, ...permanentValues }
        return {
          key,
          value: { ...currentValue, ...updater(currentValue) },
        }
      })
    } else {
      const currentValue =
        queryState.key === key ? queryState.value : { ...defaultValue, ...permanentValues }
      const updatedValue = typeof updater === "object" && updater !== null ? updater : {}
      setQueryState({ key, value: { ...currentValue, ...updatedValue } })
    }
  }

  if (queryState && queryState.key === key) {
    return [queryState.value, set, setState]
  } else {
    return [{ ...defaultValue, ...permanentValues }, set, setState]
  }
}
